/**
 * Custom event utilities for coordinating edit panel state across widgets.
 * When one edit panel opens, all other panels should close.
 */

const PANEL_OPENED_EVENT = "bc-widget-panel-opened";
const UPLOAD_ICON_REQUEST_EVENT = "bc-widget-upload-icon-request";

interface PanelOpenedDetail {
  widgetId: string;
}

/**
 * Dispatch an event when an edit panel is opened.
 * Other WidgetWrappers will listen for this and close their panels.
 */
export const dispatchPanelOpened = (widgetId: string): void => {
  const event = new CustomEvent<PanelOpenedDetail>(PANEL_OPENED_EVENT, {
    detail: { widgetId },
    bubbles: true,
  });
  document.dispatchEvent(event);
};

/**
 * Subscribe to panel opened events.
 * @param callback - Called when any panel opens, receives the widgetId that opened
 * @returns Unsubscribe function
 */
export const subscribeToPanelOpened = (
  callback: (widgetId: string) => void
): (() => void) => {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<PanelOpenedDetail>;
    callback(customEvent.detail.widgetId);
  };

  document.addEventListener(PANEL_OPENED_EVENT, handler);

  return () => {
    document.removeEventListener(PANEL_OPENED_EVENT, handler);
  };
};

/**
 * Dispatch an event requesting the icon/image picker modal to open.
 * The ThemeManager listens for this and opens the IconPickerModal.
 */
export const dispatchUploadIconRequest = (): void => {
  const event = new CustomEvent(UPLOAD_ICON_REQUEST_EVENT, { bubbles: true });
  document.dispatchEvent(event);
};

/**
 * Subscribe to upload icon request events.
 * @param callback - Called when an icon upload is requested from an edit panel
 * @returns Unsubscribe function
 */
export const subscribeToUploadIconRequest = (
  callback: () => void
): (() => void) => {
  const handler = () => {
    callback();
  };

  document.addEventListener(UPLOAD_ICON_REQUEST_EVENT, handler);

  return () => {
    document.removeEventListener(UPLOAD_ICON_REQUEST_EVENT, handler);
  };
};

